import { apiClient } from './client';

export type CaptureQualityStatus = 'ok' | 'degraded' | 'bad' | 'unknown';

/** A sensor's verdict. `absent` means the recording expected it but it was
 *  never connected — different from `bad`, which means it was there and
 *  produced nothing. */
export type CaptureQualitySensorStatus =
  | 'ok'
  | 'low'
  | 'unknown'
  | 'bad'
  | 'absent';

/** One sensor's line in the capture-quality summary (ROADMAP
 *  CAPTURE-RATE-MONITOR): which physical device it was, and how close its
 *  achieved sampling rate came to target. */
export interface CaptureQualityStream {
  stream: string;
  samples: number;
  achievedHz: number;
  targetHz: number | null;
  pctTarget: number | null;
  status: 'ok' | 'low' | 'unknown';
}

export interface CaptureQualitySensor {
  sensorId: string;
  deviceName: string | null;
  /**
   * As the device stored it: the BOTTLENECK stream's rate — the one furthest
   * from target. For a Muse that flips between EEG (256 Hz) and PPG (64 Hz)
   * on hundredths of a percent, so read `headlineHz` instead unless you
   * specifically want what the device wrote.
   */
  achievedHz: number;
  targetHz: number | null;
  /** `absent` = expected but never connected — not a failure. */
  status: 'ok' | 'low' | 'unknown' | 'bad' | 'absent';
  streams: CaptureQualityStream[];
  /**
   * The rate to SHOW: the worst EEG channel for a sensor that has EEG, the
   * stored bottleneck otherwise. Derived by the API on the way out so every
   * surface agrees and older recordings are corrected too; absent on a
   * response from an API older than that.
   */
  headlineHz?: number | null;
  headlineTargetHz?: number | null;
  headlineStatus?: 'ok' | 'low' | 'unknown' | 'bad' | 'absent' | null;
}

/** Per-session capture-quality summary the capture-app computes at dump. */
export interface CaptureQuality {
  status: CaptureQualityStatus;
  recordedSeconds: number;
  recordedFrom: string | null;
  recordedTo: string | null;
  sensors: CaptureQualitySensor[];
}

/**
 * One row in the tenant_admin's Sessions list. Recorded by the
 * capture-app via POST /api/v1/devices/me/sessions when a capture
 * session ends.
 */
export interface CaptureSession {
  id: string;
  device_id: string | null;
  /**
   * Snapshot of the device's editable label at session-end. Survives
   * a later rename / hard-delete (device_id may go NULL via the FK
   * ON DELETE SET NULL; this column stays so the row stays
   * meaningful).
   */
  device_label: string | null;
  participant_user_id: string | null;
  procedure_id: string | null;
  /**
   * The baseline this session was recorded against (ROADMAP #15).
   * Null when the capture path didn't use a baseline.
   */
  baseline_id: string | null;
  /**
   * Operator-readable identifier of the participant. Stored as a
   * denormalised snapshot so the row stays meaningful after a user
   * is deleted from the tenant.
   */
  participant_label: string | null;
  procedure_name: string;
  session_id: string;
  /** Optional human label from a split ("Closing", …); null otherwise. */
  label: string | null;
  /** ROLES-AND-GO: the role taken in this recording, as recorded. */
  role_name?: string | null;
  /** Two-stage start: seconds from the start of the recording to Go; the
   *  standby before it is left out of scores. Null = no standby. */
  go_offset_s?: number | null;
  /** How Go was given: button, voice, portal, or manual (set afterwards). */
  go_source?: string | null;
  day: number;
  session_number: number;
  /** ISO timestamps. */
  started_at: string;
  ended_at: string;
  created_at: string;
  /**
   * S3 object key for the uploaded NDJSON recording. Null when the
   * Export step was skipped on the kiosk or VITE_S3_* wasn't
   * configured. The Download button on the Sessions table gates on
   * this — if null, the button is disabled with a tooltip explaining
   * why.
   */
  s3_key: string | null;
  /**
   * Capture-quality summary (ROADMAP CAPTURE-RATE-MONITOR): per-sensor
   * device identity + achieved sampling rate + ok/degraded/bad verdict.
   * Null on rows recorded before this shipped or by a kiosk that skipped
   * Export. Drives the per-session quality badge.
   */
  capture_quality?: CaptureQuality | null;
  /**
   * Non-null when the recording is soft-deleted (disabled). Hidden from
   * the default list/feeds + excluded from scores; the "Show disabled"
   * toggle surfaces it for Restore.
   */
  deleted_at: string | null;
  /**
   * Organizational context (RFC procedure-instance, Phase 1b) — tagged by
   * a tenant_admin. Raw UUIDs into the tenant's departments / locations;
   * `kind` = the event nature; `external_case_ref` an opaque customer
   * string (no PHI). All null until tagged.
   */
  department_id?: string | null;
  location_id?: string | null;
  kind?: string | null;
  external_case_ref?: string | null;
  /** The ProcedureInstance this recording is grouped under (Phase 2), or
   *  null when ungrouped. */
  procedure_instance_id?: string | null;
  /**
   * Scoring gave up on this recording — an insight job exhausted its retries.
   * Distinct from "still processing": this one will never finish on its own.
   */
  processing_failed?: boolean;
  /** Why it gave up, verbatim from the worker. Carried alongside the flag
   *  because the two observed causes need opposite responses: a missing S3
   *  object is an upload failure worth a ticket, a recording too short to
   *  filter is simply unscoreable. */
  processing_error?: string | null;
  /**
   * Originating tenant — populated on the cross-tenant `/sessions/me`
   * feed (ROADMAP #54) so the participant Dashboard can filter by
   * tenant. Null on tenant-scoped responses.
   */
  tenant_id?: string | null;
  tenant_slug?: string | null;
  tenant_name?: string | null;
}

/** Event-kind vocabulary (RFC procedure-instance) — matches the backend
 *  SESSION_KINDS. */
export const SESSION_KINDS = [
  'live_surgery',
  'simulation',
  'training',
  'assessment',
] as const;
export type SessionKind = (typeof SESSION_KINDS)[number];

export const SESSION_KIND_LABELS: Record<SessionKind, string> = {
  live_surgery: 'Live surgery',
  simulation: 'Simulation',
  training: 'Training',
  assessment: 'Assessment',
};

export interface CaptureSessionDownload {
  url: string;
  filename: string;
  expires_in_seconds: number;
}

/** Server-side narrowing for the session roster. Everything optional; an
 *  omitted dimension means "no opinion", never "match nothing". */
export interface SessionQuery {
  /** Participant user ids — ids not names, so a rename can't change what a
   *  shared filter returns. */
  participantIds?: string[];
  procedures?: string[];
  devices?: string[];
  /** ISO or `datetime-local`; inclusive. */
  startedFrom?: string;
  startedTo?: string;
  /** `true` = only recordings scoring gave up on; `false` = only the rest;
   *  omitted = no opinion. */
  processingFailed?: boolean;
}

/** Distinct values across the tenant's WHOLE history, for the dropdowns. */
export interface SessionFilterOptions {
  participants: { id: string | null; label: string }[];
  procedures: string[];
  devices: string[];
}

/** One page of the roster (PORTAL-SESSIONS-PAGING). */
export interface CaptureSessionPage {
  sessions: CaptureSession[];
  /** Every match, across all pages. */
  total: number;
  /** Pass back as `before` for the next page; null on the last one. */
  nextCursor: string | null;
}

/**
 * The tenant's captured sessions, newest first, a page at a time. The
 * filters run on the server before paging, so `total` counts the matches
 * and every one of them is reachable with `nextCursor`.
 */
export async function listCaptureSessionsPage(
  tenantId: string,
  includeDeleted = false,
  query?: SessionQuery,
  before?: string | null,
  limit = 200,
): Promise<CaptureSessionPage> {
  const params = sessionQueryParams(includeDeleted, query);
  params.set('limit', String(limit));
  if (before) params.set('before', before);
  const { data, headers } = await apiClient.get<CaptureSession[]>(
    `/api/v1/tenants/${tenantId}/sessions`,
    { params },
  );
  const total = Number(headers['x-total-count']);
  return {
    sessions: data,
    total: Number.isFinite(total) ? total : data.length,
    nextCursor: (headers['x-next-cursor'] as string | undefined) || null,
  };
}

/** The first page only — the 200 most recent matches. Callers that need
 *  everything use listCaptureSessionsPage. */
export async function listCaptureSessions(
  tenantId: string,
  includeDeleted = false,
  query?: SessionQuery,
): Promise<CaptureSession[]> {
  const { data } = await apiClient.get<CaptureSession[]>(
    `/api/v1/tenants/${tenantId}/sessions`,
    { params: sessionQueryParams(includeDeleted, query) },
  );
  return data;
}

function sessionQueryParams(includeDeleted: boolean, query?: SessionQuery): URLSearchParams {
  // Sending the filter to the server means paging applies to the MATCHES;
  // filtering the response instead can only narrow what a page already kept.
  const params = new URLSearchParams();
  if (includeDeleted) params.set('include_deleted', 'true');
  for (const id of query?.participantIds ?? []) params.append('participant_id', id);
  for (const p of query?.procedures ?? []) params.append('procedure', p);
  for (const d of query?.devices ?? []) params.append('device', d);
  if (query?.startedFrom) params.set('started_from', query.startedFrom);
  if (query?.startedTo) params.set('started_to', query.startedTo);
  if (query?.processingFailed !== undefined)
    params.set('processing_failed', String(query.processingFailed));
  return params;
}

/**
 * The values to offer in the filters.
 *
 * Fetched rather than derived from the rows on screen: those are capped, so
 * deriving them would mean you can only filter to a participant whose
 * recordings survived the cap — and the one you need to search for is
 * exactly the one who fell off the end.
 */
export async function getSessionFilterOptions(
  tenantId: string,
  includeDeleted = false,
): Promise<SessionFilterOptions> {
  const { data } = await apiClient.get<SessionFilterOptions>(
    `/api/v1/tenants/${tenantId}/sessions/filter-options`,
    { params: includeDeleted ? { include_deleted: true } : undefined },
  );
  return data;
}

/**
 * Tag a recording with its organizational context (RFC procedure-instance,
 * Phase 1b). Send only the fields to change; explicit `null` untags one.
 */
export async function tagCaptureSession(
  tenantId: string,
  sessionRowId: string,
  body: {
    department_id?: string | null;
    location_id?: string | null;
    kind?: string | null;
    external_case_ref?: string | null;
  },
): Promise<CaptureSession> {
  const { data } = await apiClient.patch<CaptureSession>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}`,
    body,
  );
  return data;
}

/** Soft-delete (disable) a recording — hides it + excludes from scores. */
export async function disableCaptureSession(
  tenantId: string,
  sessionRowId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}`,
  );
}

/** Set (or correct) the Go point afterwards (ROLES-AND-GO): the stretch
 *  before `goOffsetS` becomes standby and leaves the scores and duration.
 *  0 = no standby. The session is re-scored in the background. */
export async function setCaptureSessionGo(
  tenantId: string,
  sessionRowId: string,
  goOffsetS: number,
): Promise<CaptureSession> {
  const { data } = await apiClient.put<CaptureSession>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/go`,
    { go_offset_s: goOffsetS },
  );
  return data;
}

/** Undo a soft-delete — bring a disabled recording back. */
export async function restoreCaptureSession(
  tenantId: string,
  sessionRowId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/restore`,
  );
}

/**
 * Request a short-lived URL to download a session's **full bundle** — a
 * ZIP of the raw NDJSON plus every video/audio attachment. The portal
 * navigates the browser to `data.url` to trigger the download.
 *
 * The server returns a *relative* API path carrying a signed token (a
 * plain anchor click sends no auth header, so the token is the gate). We
 * resolve it against the API base here so it points at the API host in
 * prod (separate `VITE_API_URL`) and stays same-origin in dev (proxy).
 * An absolute URL (legacy presigned-S3 shape) is passed through untouched.
 */
export async function getCaptureSessionDownload(
  tenantId: string,
  sessionRowId: string,
): Promise<CaptureSessionDownload> {
  const { data } = await apiClient.get<CaptureSessionDownload>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/download`,
  );
  const base = import.meta.env.VITE_API_URL ?? '';
  const url = /^https?:\/\//.test(data.url) ? data.url : `${base}${data.url}`;
  return { ...data, url };
}

/**
 * Presigned URL for the portal's in-browser session REPLAY (tenant
 * surface). Read-gated on the server (require_tenant_admin_or_super),
 * so unlike download a read-only admin gets it — replay is viewing.
 */
export async function getSessionReplayUrl(
  tenantId: string,
  sessionRowId: string,
): Promise<CaptureSessionDownload> {
  const { data } = await apiClient.get<CaptureSessionDownload>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/replay-url`,
  );
  return data;
}

/** Participant read-only: presigned NDJSON URL for a recording in a case the
 *  participant is part of — powers the Analysis 3D hand path. */
export async function getMyCaseReplayUrl(
  instanceId: string,
  sessionRowId: string,
): Promise<CaptureSessionDownload> {
  const { data } = await apiClient.get<CaptureSessionDownload>(
    `/api/v1/tenants/me/cases/${instanceId}/sessions/${sessionRowId}/replay-url`,
  );
  return data;
}

/**
 * Split one recording into N children by keep-segments (SESSION-SPLIT).
 * Each `{start_s, end_s}` (session-elapsed seconds) becomes a child
 * session that re-scores independently; gaps are dropped and the parent
 * is disabled (restorable). Operational write.
 */
export interface SplitSegmentInput {
  start_s: number;
  end_s: number;
  name?: string | null;
}

export interface SplitChild {
  id: string;
  session_id: string;
  name: string | null;
  started_at: string;
  ended_at: string;
  sample_count: number;
}

/**
 * A split runs in the background (SCALE-SPLIT-TO-WORKER): the POST answers
 * at once with a request to poll; `status` goes queued → running → done
 * (with the children) or failed (with the reason).
 */
export interface SplitStatus {
  request_id: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  parent_session_id: string;
  children: SplitChild[];
  error: string | null;
}

export async function splitCaptureSession(
  tenantId: string,
  sessionRowId: string,
  segments: SplitSegmentInput[],
): Promise<{ request_id: string; status: string }> {
  const { data } = await apiClient.post<{ request_id: string; status: string }>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/split`,
    segments,
  );
  return data;
}

export async function getSplitRequest(tenantId: string, requestId: string): Promise<SplitStatus> {
  const { data } = await apiClient.get<SplitStatus>(
    `/api/v1/tenants/${tenantId}/split-requests/${requestId}`,
  );
  return data;
}

/**
 * Latest status per insight-job kind for a session (cognitive, motion,
 * attachment_trim, thumbnails, marker_labels, …). Drives the post-split
 * "preparing video / audio" progress indicator; `processing` is true
 * while anything is still pending/running.
 */
/**
 * Tenant-wide "is any video/audio prep running" + the most informative
 * in-flight job, for the global header indicator. Polled while non-empty.
 */
export interface ActiveInsight {
  kind: string;
  label: string;
  done: number | null;
  total: number | null;
  /** Who/what the surfaced job is for — distinguishes concurrent uploads. */
  participant: string | null;
  session: string | null;
}

export interface ActiveInsights {
  processing: boolean;
  sessions: number;
  /** Overall front-runner — shown by the collapsed single-session chip. */
  active: ActiveInsight | null;
  /** One entry per in-flight session (leader-first) for the popover. */
  items: ActiveInsight[];
}

export async function getActiveInsights(
  tenantId: string,
): Promise<ActiveInsights> {
  const { data } = await apiClient.get<ActiveInsights>(
    `/api/v1/tenants/${tenantId}/insights/active`,
  );
  return data;
}

/**
 * Presigned URL for replaying the participant's OWN session. Server
 * verifies ownership (404 otherwise). Used on the participant Dashboard
 * where there's no tenant_id in hand.
 */
export async function getMySessionReplayUrl(
  sessionRowId: string,
): Promise<CaptureSessionDownload> {
  const { data } = await apiClient.get<CaptureSessionDownload>(
    `/api/v1/sessions/me/${sessionRowId}/replay-url`,
  );
  return data;
}

/**
 * Participant-facing view: the current user's own captured sessions
 * across every tenant they participate in. Auth is the regular user
 * access token; the server filters by participant_user_id =
 * current.id and returns newest-first, capped at 200.
 */
export async function listMyCaptureSessions(
  query?: Pick<SessionQuery, 'procedures' | 'startedFrom' | 'startedTo'>,
): Promise<CaptureSession[]> {
  // No participant dimension: the route is already scoped to the caller.
  const params = new URLSearchParams();
  for (const p of query?.procedures ?? []) params.append('procedure', p);
  if (query?.startedFrom) params.set('started_from', query.startedFrom);
  if (query?.startedTo) params.set('started_to', query.startedTo);
  const { data } = await apiClient.get<CaptureSession[]>(
    '/api/v1/sessions/me',
    { params },
  );
  return data;
}

/**
 * The participant's own filter options, across their whole history.
 *
 * Separate from the list for the same reason the tenant one is: once the
 * list filters server-side, deriving the dropdown from the rows would
 * collapse it to the value already chosen.
 */
export async function getMySessionFilterOptions(): Promise<{
  procedures: string[];
  devices: string[];
}> {
  const { data } = await apiClient.get<{
    procedures: string[];
    devices: string[];
  }>('/api/v1/sessions/me/filter-options');
  return data;
}

/**
 * Tenant-admin drill-down: capture sessions for one participant in
 * one tenant. Drives the Dashboard's per-participant trace card.
 */
export async function listParticipantCaptureSessions(
  tenantId: string,
  userId: string,
): Promise<CaptureSession[]> {
  const { data } = await apiClient.get<CaptureSession[]>(
    `/api/v1/tenants/${tenantId}/participants/${userId}/sessions`,
  );
  return data;
}
